import React, { lazy } from "react";

/**
 * Wraps React.lazy with a one-shot retry/reload strategy for chunk-load failures.
 *
 * When a dynamic import fails (e.g. "Importing a module script failed" — common
 * after a new deployment when the browser holds a stale HTML document that
 * references old hashed chunk filenames), this helper:
 *   1. Checks whether the page has already been force-reloaded for this error.
 *   2. If not, marks the session and calls `window.location.reload()` so the
 *      browser fetches a fresh HTML document with the correct chunk URLs.
 *   3. If a reload has already been attempted, re-throws the error so the
 *      nearest ErrorBoundary can surface it to the user.
 *
 * The session flag (`chunk-reload-attempted`) is stored in sessionStorage so
 * it resets naturally when the user closes the tab.
 */
const RELOAD_FLAG = "chunk-reload-attempted";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> => {
  return lazy((): Promise<{ default: T }> => {
    const hasReloaded = sessionStorage.getItem(RELOAD_FLAG) === "true";

    return factory().catch((error: unknown) => {
      const message =
        error instanceof Error ? error.message : String(error);

      // Only auto-reload for module-import / chunk-load errors.
      const isChunkError =
        message.includes("Importing a module script failed") ||
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Unable to preload CSS for") ||
        // Vite/Rollup chunk load errors contain "chunk" in the message
        message.toLowerCase().includes("chunk");

      if (isChunkError && !hasReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, "true");
        window.location.reload();
        // Return a never-resolving promise — the reload will take over.
        return new Promise(() => {});
      }

      // Already retried or non-chunk error — let the ErrorBoundary handle it.
      throw error;
    });
  });
};

export default lazyWithRetry;